import { useMemo, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Save } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'

import type { LotOption } from '@/components/lots/lot-items-field'
import type { CreateOutNoteItemInput } from '@/lib/api/agri-material-lot-api'
import type { AgriOutNoteFormValues } from '@/lib/schemas/agri-note-schemas'
import { PageBackButton } from '@/components/common/PageBackButton'
import { createAgriOutNoteSchema } from '@/lib/schemas/agri-note-schemas'
import { GrowerSearchSelect } from '@/components/growers/grower-search-select'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { agriLotOption } from '@/components/lots/lot-option-builders'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useAgriLotsInfinite } from '@/hooks/use-agri-material-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { m } from '@/lib/paraglide/messages'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

export interface AgriOutSubmit {
    reason: AgriOutNoteFormValues['reason']
    notes?: string
    items: CreateOutNoteItemInput[]
}

/**
 * Form phiếu xuất kho (PXK) vật tư nông nghiệp — dùng chung cho màn tạo và sửa
 * (chỉ khác `mode`). Mỗi dòng chọn 1 lô để xuất (lô thường HOẶC lô ảo 'STOCK'
 * của vật tư qty-mode — picker bật `include_virtual`); hộ trồng hiện khi lý do
 * = xuất sử dụng. Tái sử dụng LotItemsField `mode="out"` giống PXK kho bao bì.
 */
export function AgriOutNoteForm({
    mode = 'create',
    initialValues,
    seedLotOptions = [],
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: AgriOutNoteFormValues
    // Nhãn lô của phiếu khi sửa (để trigger hiển thị đúng nếu lô nằm ngoài trang
    // search hiện tại).
    seedLotOptions?: LotOption[]
    isPending: boolean
    onSubmit: (
        value: AgriOutSubmit,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
    onCancel: () => void
}) {
    const isEdit = mode === 'edit'
    const workflow = useWorkflowTracking('agricultural_stock_out', {
        operation: isEdit ? 'update' : 'create',
    })
    const [lotSearch, setLotSearch] = useState('')
    const debouncedSearch = useDebounced(lotSearch, 300)
    const lotsQuery = useAgriLotsInfinite({
        only_available: '1',
        include_virtual: '1',
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
    })
    const lotOptions = useMemo<LotOption[]>(() => {
        const fetched = (
            lotsQuery.data?.pages.flatMap((p) => p.data ?? []) ?? []
        ).map(agriLotOption)
        const byValue = new Map<string, LotOption>()
        for (const o of seedLotOptions) byValue.set(o.value, o)
        for (const o of fetched) byValue.set(o.value, o)
        return Array.from(byValue.values())
    }, [lotsQuery.data, seedLotOptions])

    const form = useForm<AgriOutNoteFormValues>({
        resolver: zodResolver(createAgriOutNoteSchema()),
        defaultValues: initialValues ?? {
            reason: 'for_use',
            notes: '',
            items: [
                { kind: 'existing', agri_material_lot_id: '', quantity: 0 },
            ],
        },
    })
    const reason = form.watch('reason')
    const forUse = reason === 'for_use'

    const submit = form.handleSubmit(
        (data) => {
            const items: CreateOutNoteItemInput[] = data.items.map((it) => ({
                agri_material_lot_id: it.agri_material_lot_id,
                ...(it.grower_id ? { grower_id: it.grower_id } : {}),
                quantity: Number(it.quantity),
            }))
            onSubmit(
                { reason: data.reason, notes: data.notes || undefined, items },
                workflow,
            )
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center gap-3">
                <PageBackButton
                    onBack={() => {
                        workflow.finish('closed')
                        onCancel()
                    }}
                />
                <h1 className="text-xl font-semibold">
                    {isEdit
                        ? m.aml_form_edit_pxk_title()
                        : m.aml_form_create_pxk_title()}
                </h1>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    onFocusCapture={() => workflow.interact('details')}
                    noValidate
                    className="max-w-5xl space-y-6"
                >
                    <div className="grid gap-4 rounded-md border bg-card p-4 md:grid-cols-2">
                        <div>
                            <Label>{m.aml_form_reason()}</Label>
                            <Select
                                value={reason}
                                disabled={isEdit}
                                onValueChange={(v) =>
                                    form.setValue(
                                        'reason',
                                        v as AgriOutNoteFormValues['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="for_use">
                                        {m.aml_out_reason_for_use()}
                                    </SelectItem>
                                    <SelectItem value="discard">
                                        {m.aml_out_reason_discard()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.aml_out_reason_adjustment()}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{m.aml_form_notes()}</Label>
                            <Input {...form.register('notes')} />
                        </div>
                    </div>

                    <LotItemsField
                        mode="out"
                        lotOptions={lotOptions}
                        categoryOptions={[]}
                        categoryLabel={m.aml_form_material()}
                        existingIdField="agri_material_lot_id"
                        onLotSearchChange={setLotSearch}
                        onLotLoadMore={() => lotsQuery.fetchNextPage()}
                        hasMoreLots={lotsQuery.hasNextPage}
                        loadingMoreLots={lotsQuery.isFetchingNextPage}
                        renderExtraPerItem={
                            forUse
                                ? (idx) => <GrowerRow idx={idx} />
                                : undefined
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={() => {
                                workflow.finish('closed')
                                onCancel()
                            }}
                        >
                            {m.aml_form_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="mr-1 size-4" />
                            {m.aml_form_save()}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}

/** Ô chọn hộ trồng cho 1 dòng item — render qua slot renderExtraPerItem của
 *  LotItemsField (nằm trong cùng FormProvider). */
function GrowerRow({ idx }: { idx: number }) {
    const { watch, setValue } = useFormContext<AgriOutNoteFormValues>()
    const growerId = watch(`items.${idx}.grower_id`)
    const growerName = watch(`items.${idx}.grower_name`)
    return (
        <div>
            <Label className="text-xs">{m.aml_form_grower()}</Label>
            <GrowerSearchSelect
                value={growerId}
                fallbackLabel={growerName}
                onChange={(gid) =>
                    setValue(`items.${idx}.grower_id`, gid || undefined)
                }
                placeholder={m.aml_form_grower_ph()}
            />
        </div>
    )
}
